import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { format } from 'date-fns';
import { Plus, Search, MapPin, Users, Briefcase, ChevronRight, Filter } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface Campaign {
  id: string;
  title: string;
  country: string;
  company_name: string;
  status: 'draft' | 'active' | 'selection_done' | 'completed' | 'paused';
  created_at: string;
  applications_count: number;
  jobs_count: number;
}

const statusConfig: Record<string, { label: string; classes: string }> = {
  active: { label: 'Active', classes: 'bg-emerald-50 text-emerald-700' },
  draft: { label: 'Draft', classes: 'bg-slate-100 text-slate-500' },
  selection_done: { label: 'Selection Done', classes: 'bg-blue-50 text-blue-700' },
  completed: { label: 'Completed', classes: 'bg-violet-50 text-violet-600' },
  paused: { label: 'Paused', classes: 'bg-amber-50 text-amber-600' },
  blocked: { label: 'Blocked', classes: 'bg-red-50 text-red-600' },
};

export default function Campaigns() {
  const { activeUserId } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserId]);

  const fetchCampaigns = async () => {
    if (!activeUserId) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          id, title, country, company_name, status, created_at,
          jobs ( id, applications (id) )
        `)
        .eq('recruiter_id', activeUserId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formatted = (data || []).map((camp: any) => ({
        id: camp.id,
        title: camp.title,
        country: camp.country,
        company_name: camp.company_name,
        status: camp.status,
        created_at: camp.created_at,
        jobs_count: camp.jobs?.length || 0,
        applications_count: camp.jobs?.reduce((acc: number, job: any) => acc + (job.applications?.length || 0), 0) || 0
      }));

      setCampaigns(formatted);
    } catch (err) {
      console.error('Error fetching campaigns', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = campaigns.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.company_name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusTabs = ['all', 'active', 'draft', 'selection_done', 'completed'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Campaigns</h1>
          <p className="text-slate-400 mt-1 text-[15px]">{campaigns.length} total campaigns</p>
        </div>
        <Link
          to="/app/campaigns/new"
          className="flex items-center gap-2 bg-slate-950 text-white text-[13px] font-semibold px-5 py-2.5 rounded-xl hover:bg-slate-800 transition-colors shadow-sm"
        >
          <Plus size={16} />
          New Campaign
        </Link>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total', value: campaigns.length, icon: Briefcase, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Active', value: campaigns.filter(c => c.status === 'active').length, icon: Filter, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Applicants', value: campaigns.reduce((s, c) => s + c.applications_count, 0), icon: Users, color: 'text-violet-600', bg: 'bg-violet-50' },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm flex items-center gap-4">
              <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center ${s.color}`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="text-xl font-black text-slate-900 tracking-tight">{s.value}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        {/* Status pills */}
        <div className="flex gap-2 flex-wrap">
          {statusTabs.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${statusFilter === s
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                }`}
            >
              {s === 'all' ? 'All' : (statusConfig[s]?.label || s)}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs ml-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 transition-all"
          />
        </div>
      </div>

      {/* Campaign Cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
          <Briefcase size={40} className="mx-auto text-slate-200 mb-3" />
          <p className="text-[15px] font-semibold text-slate-700">No campaigns found</p>
          <p className="text-[13px] text-slate-400 mt-1">Try adjusting your filters or create a new campaign.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(camp => {
            const cfg = statusConfig[camp.status] || statusConfig.draft;
            return (
              <div
                key={camp.id}
                onClick={() => navigate(`/app/campaigns/${camp.id}`)}
                className="group flex items-center bg-white rounded-2xl px-5 py-4 border border-slate-200/60 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
              >
                <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform shrink-0">
                  <Briefcase size={16} className="text-slate-500" />
                </div>

                <div className="flex-1 min-w-0 mr-4">
                  <p className="text-[13px] font-semibold text-slate-800 group-hover:text-blue-600 transition-colors truncate">{camp.title}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
                    <span className="flex items-center gap-1"><Briefcase size={11} /> {camp.company_name}</span>
                    <span className="flex items-center gap-1"><MapPin size={11} /> {camp.country}</span>
                    <span className="hidden sm:inline">{format(new Date(camp.created_at), 'MMM d, yyyy')}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${cfg.classes}`}>
                    {cfg.label}
                  </span>
                  <div className="text-center mr-2">
                    <p className="text-[14px] font-bold text-slate-800">{camp.jobs_count}</p>
                    <p className="text-[9px] text-slate-400 font-semibold uppercase">Roles</p>
                  </div>
                  <div className="text-center mr-2">
                    <p className="text-[14px] font-bold text-slate-800">{camp.applications_count}</p>
                    <p className="text-[9px] text-slate-400 font-semibold uppercase">Applied</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
